/**
 * `events` procedure group — CRM-level events ("SaaStr 2026", "Wednesday dinner").
 *
 * Distinct from provider `calendar_events`: an event is the thing a contact was *met
 * at*, which may or may not have originated from a synced calendar row.
 */

import { z } from 'zod';
import { isoDateTimeSchema, nonBlankString, paginationInputSchema, uuidSchema } from './common.ts';
import { eventCandidateReasonSchema, eventKindSchema, eventSourceSchema } from './enums.ts';

/** `events.current` never returns more than this many candidates. */
export const MAX_CURRENT_EVENT_CANDIDATES = 3;

/** Default half-width of the "am I at this right now" window, in minutes. */
export const DEFAULT_CURRENT_EVENT_WINDOW_MINUTES = 15;

export const eventSchema = z.object({
  id: uuidSchema,
  orgId: uuidSchema,
  name: z.string(),
  kind: eventKindSchema,
  source: eventSourceSchema,
  calendarEventId: uuidSchema.nullable(),
  startsAt: isoDateTimeSchema.nullable(),
  endsAt: isoDateTimeSchema.nullable(),
  locationText: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  notes: z.string().nullable(),
  contactCount: z.int().min(0),
  createdBy: uuidSchema.nullable(),
  createdAt: isoDateTimeSchema,
});

/**
 * A ranked "you are probably here" suggestion for the add-contact met-at field.
 * Served from the local `calendar_events` mirror — never a live provider call.
 */
export const eventCandidateSchema = z.object({
  event: eventSchema,
  /** 0..1; the client pre-selects the top candidate but always shows an escape. */
  score: z.number().min(0).max(1),
  reason: eventCandidateReasonSchema,
  /** Candidates with resolved attendees outrank bare time overlaps. */
  attendeeCount: z.int().min(0),
  /** Set when the candidate was promoted from a calendar row rather than a CRM event. */
  calendarEventId: uuidSchema.nullable(),
});

// --- inputs ------------------------------------------------------------------

export const eventListInputSchema = paginationInputSchema.extend({
  q: z.string().min(1).max(200).optional(),
  kind: z.array(eventKindSchema).max(5).optional(),
  source: eventSourceSchema.optional(),
  /** Inclusive lower/upper bounds on `startsAt`. */
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
});

export const eventGetInputSchema = z.object({
  id: uuidSchema,
});

export const eventCreateInputSchema = z.object({
  name: nonBlankString(200, 'name'),
  kind: eventKindSchema.default('meeting'),
  startsAt: isoDateTimeSchema.nullish(),
  endsAt: isoDateTimeSchema.nullish(),
  locationText: z.string().max(500).nullish(),
  city: z.string().max(200).nullish(),
  country: z.string().max(200).nullish(),
  notes: z.string().max(20_000).nullish(),
  /** Promotes an existing provider row instead of creating a bare manual event. */
  calendarEventId: uuidSchema.nullish(),
});

export const eventsCurrentInputSchema = z.object({
  /** Defaults to server `now`; supplied by clients so an offline queue can backfill. */
  at: isoDateTimeSchema.optional(),
  windowMinutes: z.int().min(1).max(240).default(DEFAULT_CURRENT_EVENT_WINDOW_MINUTES),
});

/** 0..3 candidates, best first. An empty array means "not at an event" — not an error. */
export const eventsCurrentOutputSchema = z.object({
  candidates: z.array(eventCandidateSchema).max(MAX_CURRENT_EVENT_CANDIDATES),
  asOf: isoDateTimeSchema,
});

// --- inferred types ----------------------------------------------------------

export type CrmEvent = z.output<typeof eventSchema>;
export type EventCandidate = z.output<typeof eventCandidateSchema>;
export type EventsCurrentOutput = z.output<typeof eventsCurrentOutputSchema>;
export type EventCreateInput = z.input<typeof eventCreateInputSchema>;
